import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Buyer-facing post-payment landing page.
 *
 * - Reached only via the unique session_id Stripe redirects to after a
 *   successful payment. Not linked anywhere on the site.
 * - <meta name="robots" content="noindex,nofollow"> stops it being indexed.
 * - Reveals nothing about the artwork or the price — only that payment was
 *   received. Sale records live in the admin transactions ledger.
 * - Polls /api/payments/checkout/status/{session_id} until paid or timeout
 *   so MongoDB / artwork / consignment side effects fire even if the
 *   Stripe webhook is delayed.
 */
export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking'); // checking | paid | pending | error
  const [errorMsg, setErrorMsg] = useState(null);
  const attempts = useRef(0);

  // Replace any existing robots meta with noindex on mount, restore on unmount
  useEffect(() => {
    let m = document.head.querySelector('meta[name="robots"]');
    const created = !m;
    let prev = null;
    if (m) {
      prev = m.getAttribute('content');
    } else {
      m = document.createElement('meta');
      m.setAttribute('name', 'robots');
      document.head.appendChild(m);
    }
    m.setAttribute('content', 'noindex,nofollow');
    const prevTitle = document.title;
    document.title = 'Thank you — Zebra One Gallery';
    return () => {
      if (created) {
        m.remove();
      } else if (prev !== null) {
        m.setAttribute('content', prev);
      }
      document.title = prevTitle;
    };
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      setErrorMsg('Missing session reference.');
      return;
    }
    let cancelled = false;
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await fetch(`${API}/api/payments/checkout/status/${encodeURIComponent(sessionId)}`);
        if (!r.ok) throw new Error(`Status check failed (${r.status})`);
        const data = await r.json();
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setState('paid');
          return;
        }
        if (data.status === 'expired') {
          setState('error');
          setErrorMsg('This payment link has expired.');
          return;
        }
        if (attempts.current >= 8) {
          setState('pending');
          return;
        }
        setTimeout(poll, 2500);
      } catch (e) {
        if (cancelled) return;
        if (attempts.current >= 8) {
          setState('error');
          setErrorMsg(e.message);
        } else {
          setTimeout(poll, 2500);
        }
      }
    };
    poll();
    return () => { cancelled = true; };
    // `attempts` is a useRef (stable). `API` is a module constant.
    // Effect should re-run only when sessionId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md text-center space-y-6">
        {state === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-gray-400 animate-spin" />
            <h1 className="text-3xl font-light">Confirming your payment…</h1>
            <p className="text-gray-500">One moment, please.</p>
          </>
        )}

        {state === 'paid' && (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
            <h1 className="text-3xl font-light" data-testid="payment-thankyou-heading">Thank you</h1>
            <p className="text-gray-700">
              Your payment has been received. We will be in touch shortly to
              arrange delivery and provide your certificate of authenticity.
            </p>
            <p className="text-sm text-gray-400 pt-4">— Zebra One Gallery</p>
          </>
        )}

        {state === 'pending' && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-amber-500" />
            <h1 className="text-3xl font-light">Payment processing</h1>
            <p className="text-gray-700">
              Your bank is still finalising the transaction. You'll receive
              an email confirmation as soon as it clears.
            </p>
            <p className="text-sm text-gray-400 pt-4">— Zebra One Gallery</p>
          </>
        )}

        {state === 'error' && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="text-3xl font-light">We couldn't confirm your payment</h1>
            <p className="text-gray-700">
              {errorMsg || 'Please contact info@zebraonegallery.com if you believe this is a mistake.'}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
